<template>
  <ConnectEtsyShop v-if="user.connected_shops === 0" v-slot="{ showConnectShopDialog }" module="RefreshDataButton">
    <Button
      class="ml-auto hidden bg-[--v3-button-linkbutton-forestgreen] hover:bg-[--v3-progress-bar-progressbarvaluebg] md:flex"
      label="Connect Shop"
      icon="plus"
      icon-class="size-[12px]"
      outlined
      severity="tertiary"
      @click="showConnectShopDialog"
    />
  </ConnectEtsyShop>
  <div v-else>
    <button v-if="isTokenCheckerLoading" class="text-small-medium ml-auto hidden items-center gap-1 p-0 text-[--color-neutral-neutral-light] disabled:text-[--color-neutral-neutral-light] md:flex" disabled>
      Loading...
    </button>
    <button
      v-else-if="!isTokenRevoked"
      v-tooltip.bottom="'Refresh your listing data'"
      class="text-small-medium ml-auto hidden items-center gap-1 p-0 text-[--color-brand-forrest-green-80] hover:text-[--color-brand-forrest-green] disabled:text-[--color-neutral-neutral-light] md:flex"
      :disabled="isSyncing"
      @click="refreshListings"
    >
      <Icon name="sync-up" class="size-4" :class="isSyncing ? 'animate-spin direction-reverse' : ''" />
      {{ isSyncing ? 'Syncing...' : 'Sync Listings' }}
    </button>
    <ContextualHelp
      v-else-if="isTokenRevoked"
      content="Looks like your shop connection needs a quick refresh."
      title="Reconnect your shop"
      :link="{
        label: 'Please reconnect your shop',
        url: '/settings'
      }"
    >
      <template #trigger>
        <button
          class="text-small-medium ml-auto hidden items-center gap-1 p-0 text-[--color-brand-forrest-green-80] hover:text-[--color-brand-forrest-green] disabled:text-[--color-neutral-neutral-light] md:flex"
          disabled
        >
          <Icon name="sync-up" class="size-4" :class="isSyncing ? 'animate-spin direction-reverse' : ''" />
          {{ isSyncing ? 'Syncing...' : 'Sync Listings' }}
        </button>
      </template>
    </ContextualHelp>
    <div class="hidden items-center gap-1 md:flex">
      <div v-if="isLoadingLastSync" class="h-3 w-28 animate-pulse rounded bg-gray-300" />
      <span v-else class="text-tiny-medium flex items-center gap-1" :class="syncTextColorClass"><Icon v-if="lastSync > 720" name="warning-sign" class="size-4" /> Synced {{ hoursAgo }}</span>
    </div>
    <button
      v-tooltip.bottom="'Refresh your shop data'"
      class="flex text-black disabled:text-gray-400 md:hidden"
      :disabled="isSyncing"
      @click="refreshListings"
    >
      <Icon name="sync-up" class="size-5" :class="isSyncing ? 'animate-spin direction-reverse' : ''" />
    </button>
  </div>
  <Dialog
    v-model:visible="showWarningDialog"
    header="Edit Profile"
    :style="{ width: '387px', marginTop: '68px' }"
    position="topright"
    :modal="false"
    :draggable="false"
    append-to="body"
    :pt="{
      root: 'border-none',
      header: 'v3-small-normal-line-height-150 pb-[0.8rem]',
      title: 'text-[black]',
      icons: 'absolute top-[8px] right-[8px]'
    }"
    @hide="closeWarningDialog"
  >
    <template #closeicon>
      <Icon name="x" class="size-3 text-[--color-neutral-neutral-lighter]" />
    </template>
    <template #header>
      <div class="flex items-center gap-2">
        <Icon name="warning-sign" class="size-6 text-[--color-system-error-red]" />
        <h5 class="text-medium-semi-bold text-black">
          Your listings are out of date
        </h5>
      </div>
    </template>
    <template #default>
      <p class="text-small-normal !text-[--color-neutral-neutral]">
        Your stats may be outdated. Run a sync to update your listing data.
      </p>
      <div class="flex justify-end pt-4">
        <Button
          class="!h-[48px] w-[125px] border-[--color-system-error-red] bg-[--color-system-error-red] py-3 hover:bg-[--color-system-error-red-dark]"
          label="Sync Now"
          severity="danger"
          label-class="text-regular-medium"
          @click="triggerRefreshListings"
        />
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { useCookies } from '@vueuse/integrations/useCookies';
import { storeToRefs } from 'pinia';
import Dialog from 'primevue/dialog';
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';

import Button from '@/common/components/atoms/Button';
import Icon from '@/common/components/atoms/Icon';
import ContextualHelp from '@/common/components/molecules/ContextualHelp';
import ConnectEtsyShop from '@/common/components/organisms/ConnectEtsyShop';
import { mixPanelEvents } from '@/common/constants';
import { useAuthStore } from '@/common/stores/auth';
import { useTrackedToast } from '@/composables/useTrackedToast';
import { track } from '@/helpers/analytics/mixpanel';
import { useTopbarStore } from '@/modules/layout/stores/topbar';
import { useListingTable } from '@/modules/listings/stores/ListingTableStore';

const toast = useTrackedToast();
const route = useRoute();
const cookies = useCookies();
const topbarStore = useTopbarStore();
const { closeWarningDialog } = topbarStore;
const { isSyncing, lastSync, isLoadingLastSync, showWarningDialog } = storeToRefs(topbarStore);
const authStore = useAuthStore();
const { checkTokenValidity } = authStore;
const { user, isTokenRevoked, isTokenCheckerLoading } = storeToRefs(authStore);
const listingTableStore = useListingTable();
const { getListingItems } = listingTableStore;

onMounted(() => {
  if(user.value.connected_shops != 0) {
    checkTokenValidity();
  }
  
  pollJobStatus();

  // Check if we need to refresh listings after connecting a shop
  const etsyShopConnected = localStorage.getItem('etsyShopConnected');
  if(etsyShopConnected === 'success'){
    // Trigger refresh data
    refreshListings();
    // Remove local storage flag
    localStorage.removeItem('etsyShopConnected');
  }
});

const hoursAgo = computed(() =>{
  return lastSync.value <= 0 ? 'a moment ago' : formatHoursToDays(lastSync.value) + ' ago';
});

const syncTextColorClass = computed(() => {
  const hours = lastSync.value;
  if (hours <= 72) return 'text-[--color-neutral-neutral-light]';
  if (hours <= 720) return 'text-[--color-system-warning-yellow]';
  return 'text-[--color-system-error-red]';
});

const triggerRefreshListings = () => {
  showWarningDialog.value = false;
  refreshListings();
};

const pollJobStatus = async () => {
  try {
    const uuid = cookies.get('refresh_job_uuid');
    if (!uuid) return;
    isSyncing.value = true;
    let jobCompleted = false;
    while (!jobCompleted) {
      const jobStatus = await topbarStore.checkRefreshJobStatus(uuid);
      if (jobStatus?.status === 404) {
        cookies.remove('refresh_job_uuid', { path: '/' });
        isSyncing.value = false;
        jobCompleted = true;
        break;
      }
      const { progress_percentage, is_failed } = jobStatus.data.data;
      if (is_failed) {
        cookies.remove('refresh_job_uuid', { path: '/' });
        toast.add({
          severity: 'error',
          summary: 'Error',
          detail: "We couldn't refresh your listing data. Please try again.",
          group: 'top-right',
          errorCode: 'REFRESH_JOB_FAILED',
        });
        jobCompleted = true;
        isSyncing.value = false;
        break;
      }
      if (progress_percentage >= 100) {
        topbarStore.deleteRefreshJob(uuid);
        topbarStore.getLastRefreshTime();
        topbarStore.processHealthCheck({
          shop_id: user.value.current_shop_id,
        });
        cookies.remove('refresh_job_uuid', { path: '/' });
        if (route.path.startsWith('/listings')) {
          window.location.reload();
          break;
        }
        await getListingItems();
        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Data refreshed successfully',
          group: 'top-right',
        });
        jobCompleted = true;
        isSyncing.value = false;
        if (route.name === 'health-check') {
          window.location.reload();
        }
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 2000)); // Poll every 2 seconds
    }
  } catch (error) {
    cookies.remove('refresh_job_uuid', { path: '/' });
    isSyncing.value = false;
  }
};
    
const refreshListings = async () => {
  try {
    isSyncing.value = true;
    track(mixPanelEvents.CLICKED_SYNC_NOW, {
      shop: user.value.current_shop.shop_name,
      shop_id: user.value.current_shop_id,
    });
    const uuidResponse = await topbarStore.refreshListings({
      shop_id: user.value.current_shop_id,
      member_id: user.value.member_id,
      shop_name: user.value.current_shop.shop_name,
    });
    if (uuidResponse.status !== 200) {
      const statusCode = uuidResponse.status ? `${uuidResponse.status}` : '';
      const errorCode = statusCode ? `${statusCode} REFRESH_DATA_FAILED` : 'REFRESH_DATA_FAILED';
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to refresh data',
        life: 3000,
        group: 'top-right',
        errorCode,
      });
      return;
    }

    cookies.set('refresh_job_uuid', uuidResponse.data.data.uuid, { 
      path: '/',
      maxAge: 30 * 60,
    });

    await pollJobStatus();
  } catch (error) {
    const statusCode = error.response?.status ? `${error.response.status}` : '';
    const errorCode = statusCode ? `${statusCode} REFRESH_DATA_FAILED` : 'REFRESH_DATA_FAILED';
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to refresh data',
      life: 3000,
      group: 'top-right',
      errorCode,
    });
  } finally {
    isSyncing.value = false;
  }
};

/**
 * Formats a number of hours into a string displaying days or hours.
 * @param {number} totalHours - The total number of hours.
 * @returns {string} The formatted time string (e.g., "2 days", "23 hours").
 */
const formatHoursToDays = (totalHours) => {
  // Ensure the input is a valid, non-negative number
  const hours = Math.max(0, Number(totalHours));

  if (hours >= 24) {
    // If 24 hours or more, convert to days
    const days = Math.floor(hours / 24);

    let result = `${days} day${days > 1 ? 's' : ''}`;

    return result;
  } else {
    // If less than 24 hours, show in hours
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }
};
</script>

<style lang="scss" scoped>
.refresh-btn {
  background: var(--v3-erank-orangepizazz);
  border-color: var(--v3-erank-orangepizazz);
  padding: 0.75rem;
  &:hover {
    background: var(--v3-erank-carrot);
    border-color: var(--v3-erank-carrot);
  }
}
</style>
